import { formatDateTime, formatFileSize } from "@/lib/clients";
import { FileThumb } from "@/components/FileThumb";
import { DOC_TYPES, DEFAULT_DOC_TYPE, docTypeColor } from "@/lib/files";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { ExternalLink, Download, Trash2, Cloud, CloudOff } from "lucide-react";

export function FileRow({ file, onOpen, onDownload, onDelete, onThumbClick, onRecategorize, canDelete = true, canView = true }) {
  const docType = file.doc_type || DEFAULT_DOC_TYPE;
  const linkGone = Boolean(file.sp_link_deleted);
  const openInSharePoint = Boolean(file.sp_web_url) && !linkGone;
  const handleDocClick = () => {
    if (openInSharePoint) window.open(file.sp_web_url, "_blank", "noopener,noreferrer");
    else if (onThumbClick) onThumbClick(file);
    else if (onOpen) onOpen(file);
  };
  return (
    <div className="flex items-center gap-3 p-2.5 border border-gray-100 rounded-sm hover:bg-gray-50 transition-colors group" data-testid={`file-row-${file.id}`}>
      <FileThumb file={file} onClick={handleDocClick} />
      <div className="flex-1 min-w-0">
        <button onClick={handleDocClick} type="button" data-testid={`document-link-${file.id}`}
          title={openInSharePoint ? "Open in SharePoint" : "Open"}
          className="text-sm font-semibold text-gray-900 truncate text-left hover:text-[#0078D4] hover:underline max-w-full">
          {file.original_filename}
        </button>
        <p className="text-xs text-gray-400">{formatFileSize(file.size)} · {formatDateTime(file.created_at)}</p>
        {linkGone && (
          <p className="text-xs font-semibold text-red-600 flex items-center gap-1 mt-0.5" data-testid={`sp-link-removed-${file.id}`}>
            <CloudOff className="w-3 h-3" /> SharePoint file deleted
            {file.sp_link_deleted_detected_by && <span className="font-normal text-red-400"> · noted by {file.sp_link_deleted_detected_by}{file.sp_link_deleted_at ? ` on ${formatDateTime(file.sp_link_deleted_at)}` : ""}</span>}
          </p>
        )}
      </div>
      {onRecategorize ? (
        <Select value={docType} onValueChange={(v) => onRecategorize(file.id, v)}>
          <SelectTrigger className="h-7 w-auto min-w-[140px] text-xs rounded-sm border-gray-200 px-2 shrink-0"
            data-testid={`file-doctype-trigger-${file.id}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {DOC_TYPES.map((t) => (
              <SelectItem key={t.value} value={t.value} className="text-xs"
                data-testid={`file-doctype-option-${file.id}-${t.value}`}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <span className={`text-xs font-semibold px-2 py-0.5 rounded-sm shrink-0 ${docTypeColor(docType)}`}
          data-testid={`file-doctype-badge-${file.id}`}>
          {DOC_TYPES.find((t) => t.value === docType)?.label || docType}
        </span>
      )}
      <div className="flex items-center gap-1 shrink-0">
        {canView && !linkGone && (
          <>
            <button onClick={() => onOpen(file)} title="Open in default app"
              data-testid={`open-file-${file.id}`} className="p-1.5 rounded-sm text-gray-400 hover:text-[#002FA7] hover:bg-blue-50">
              <ExternalLink className="w-4 h-4" />
            </button>
            <button onClick={() => onDownload(file)} title="Download"
              data-testid={`download-file-${file.id}`} className="p-1.5 rounded-sm text-gray-400 hover:text-[#002FA7] hover:bg-blue-50">
              <Download className="w-4 h-4" />
            </button>
          </>
        )}
        {file.sp_web_url && !linkGone && (
          <a href={file.sp_web_url} target="_blank" rel="noopener noreferrer" title="Open in SharePoint"
            data-testid={`sharepoint-file-${file.id}`} className="p-1.5 rounded-sm text-gray-400 hover:text-[#0078D4] hover:bg-blue-50">
            <Cloud className="w-4 h-4" />
          </a>
        )}
        {canDelete && (
          <button onClick={() => onDelete(file)} title="Remove" data-testid={`delete-file-${file.id}`}
            className="p-1.5 rounded-sm text-gray-400 hover:text-red-600 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity">
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
